// Visits list — RON-5466 Phase 1.
//
// Mirrors the ListScreen in the prototype: search, select-all, per-row actions and a bulk
// action bar. Table markup uses the house `table-details` class so it matches Colleagues and
// MyBooking; only the visitor-specific pieces (badges, bulk bar) use BEM.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import {
  VISIT_TYPE,
  canCancelVisit,
  canEditVisit,
  pluralText,
} from "../visitorConstants.js";
import {
  VisitorAvatar,
  VisitorKindBadge,
  VisitorProvisionalBadge,
  VisitorStatusBadge,
  VisitorVipBadge,
} from "./VisitorBadges.jsx";

const VisitorList = ({
  visits,
  isLoading,
  // Search and paging live in the route shell, because the term goes to the SERVER (§4.4) and the
  // component that talks to the API has to own it. This list renders exactly what it is handed.
  query = "",
  onQueryChange,
  isSearching = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  total = 0,
  canInviteVisitor,
  canInviteContractor,
  onInviteVisitor,
  onInviteContractor,
  onView,
  onEdit,
  onCancel,
  onBulkEdit,
  onBulkCancel,
}) => {
  const { t } = useTranslation("common");

  // Selection stays local. The shell never needs to read it — it receives the ids through
  // `onBulkEdit` / `onBulkCancel` — so lifting it would add a controlled-component round trip for
  // nothing.
  const [selectedIds, setSelectedIds] = useState([]);

  // But it MUST be dropped when the search changes. The rows behind a selection disappear when the
  // server answers a new term, and a hidden selection that is still live would let a host cancel
  // people they can no longer see.
  useEffect(() => {
    setSelectedIds([]);
  }, [query]);

  // Selection drives the bulk bar, whose destructive action is Cancel — so a row is selectable
  // exactly when the server says it is cancellable.
  const selectableIds = useMemo(
    () => visits.filter(canCancelVisit).map((visit) => visit.id),
    [visits]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedSet.has(id));

  // `canEdit` and `canCancel` are two independent server flags, so a selectable row is not
  // necessarily an editable one — bulk edit is offered only when every selected row can actually
  // be edited. Without this a mixed selection would silently skip rows.
  const selectionIsEditable = useMemo(
    () =>
      selectedIds.length > 0 &&
      visits.filter((visit) => selectedSet.has(visit.id)).every(canEditVisit),
    [visits, selectedSet, selectedIds.length]
  );

  const toggleOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id]
    );

  // ── Lazy loading ────────────────────────────────────────────────────────────────────────────
  //
  // The observer is re-created whenever `onLoadMore` changes, which it does on every page — that
  // is deliberate. A stale closure here would keep asking for the same page forever.
  //
  // `rootMargin` starts the fetch a screen early, so the next rows are usually already there by
  // the time the host reaches the bottom rather than appearing after a visible pause.
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;

    // No node means there is nothing more to load. No IntersectionObserver means jsdom or an old
    // browser — the button inside the sentinel is then the only way forward, which is why it is
    // always rendered rather than being a `<noscript>`-style afterthought.
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore?.();
      },
      { rootMargin: "300px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableIds);
  const clearSelection = () => setSelectedIds([]);

  const runBulk = (handler) => {
    handler?.(selectedIds);
    clearSelection();
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={8} className="visitor-list__empty">
            {t("react_loading")}
          </td>
        </tr>
      );
    }

    if (visits.length === 0) {
      return (
        <tr>
          <td colSpan={8} className="visitor-list__empty">
            {/* Two different facts, and conflating them is the bug §4.4 warns about: "no matches
                for this search" must never read as "you have no visitors". The search term is
                what tells them apart, because the filtering happens server-side now. */}
            {query.trim() === ""
              ? t("react_visitors_empty")
              : t("react_visitors_no_matches")}
          </td>
        </tr>
      );
    }

    return visits.map((visit) => {
      const editable = canEditVisit(visit);
      const cancellable = canCancelVisit(visit);
      const isSelected = selectedSet.has(visit.id);

      return (
        <tr key={visit.id} className={isSelected ? "visitor-list__row--selected" : ""}>
          <td className="visitor-list__col-check">
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!cancellable}
              onChange={() => toggleOne(visit.id)}
              aria-label={visit.name}
            />
          </td>
          <td>
            <span className="visitor-list__name">
              <VisitorAvatar name={visit.name} type={visit.type} />
              <span className="visitor-list__name-text">{visit.name}</span>
              {visit.type === VISIT_TYPE.VIP && <VisitorVipBadge />}
              {visit.isProvisional && <VisitorProvisionalBadge />}
            </span>
          </td>
          <td>
            <VisitorKindBadge kind={visit.kind} />
          </td>
          <td>
            <span>{visit.company}</span>
          </td>
          <td>
            <span>{formatDateToEEEddMMM(visit.startDate)}</span>
          </td>
          <td>
            <span>{visit.arrivalTime}</span>
          </td>
          <td>
            <VisitorStatusBadge status={visit.status} />
          </td>
          <td>
            <span className="visitor-list__row-actions">
              <button
                type="button"
                className="visitor-list__row-btn"
                disabled={!editable}
                onClick={() => onEdit?.(visit)}
              >
                {t("edit")}
              </button>
              <button
                type="button"
                className="visitor-list__row-btn visitor-list__row-btn--danger"
                disabled={!cancellable}
                onClick={() => onCancel?.(visit)}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className="visitor-list__row-btn"
                onClick={() => onView?.(visit)}
              >
                {t("react_visitors_view")}
              </button>
            </span>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="visitor-list">
      <div className="visitor-list__toolbar">
        <div className="visitor-list__intro">
          {/* Prototype heading is "Visitors & contractors" — the sidebar item stays "Visitors". */}
          <h2 className="module-title">{t("react_visitors_list_title")}</h2>
          <p className="visitor__subtitle">{t("react_visitors_subtitle")}</p>
        </div>

        <div className="visitor-list__actions">
          {canInviteContractor && (
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary"
              onClick={onInviteContractor}
            >
              {t("react_visitors_invite_contractor")}
            </button>
          )}
          {canInviteVisitor && (
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary"
              onClick={onInviteVisitor}
            >
              {t("react_visitors_invite_visitor")}
            </button>
          )}
        </div>
      </div>

      {/* `form-row` is what keeps the input's border on focus — see VisitorFormRow.jsx. */}
      <div className="visitor-list__search form-row">
        <input
          type="text"
          className="form-control"
          value={query}
          onChange={(event) => onQueryChange?.(event.target.value)}
          placeholder={t("react_visitors_search_placeholder")}
          aria-label={t("react_visitors_search_placeholder")}
        />
        {/* The debounce means there is a moment where the rows on screen no longer match what has
            been typed. Saying so beats showing stale rows as though they were results. */}
        {isSearching && (
          <p className="visitor-list__searching">{t("react_loading")}</p>
        )}
      </div>

      <div className="visitor-list__table-wrap">
        {/* `table-details` only (no `gridtable`): gridtable forces `table-layout: fixed` plus a
            permanently visible tbody scrollbar, which clipped the Actions column and misaligned
            the header. This list scrolls with the page instead. */}
        <table className="table-details visitor-list__table">
          <thead>
            <tr>
              <th className="visitor-list__col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={selectableIds.length === 0}
                  onChange={toggleAll}
                  aria-label={t("react_visitors_select_all")}
                />
              </th>
              <th className="visitor-list__col-name">{t("name")}</th>
              <th>
                {t("react_visitors_col_type")}
              </th>
              <th className="visitor-list__col-company">{t("company")}</th>
              <th>{t("date")}</th>
              <th>{t("time")}</th>
              <th>{t("status")}</th>
              <th className="visitor-list__col-actions">
                {t("react_visitors_col_actions")}
              </th>
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>

        {/* ── Lazy loading ──────────────────────────────────────────────────────────────────
            The sentinel sits under the table; when it scrolls into view the next page is
            requested. It is rendered only while there is more to fetch, so the observer stops
            firing at the end of the list instead of being disconnected on a timer.

            The button is not decoration. `IntersectionObserver` is absent in jsdom and in older
            browsers, and a list that can only be advanced by a scroll event nobody can trigger is
            a list with no way to reach page two — so when the observer is unavailable, the button
            is the mechanism rather than a fallback. It is also what keyboard and screen-reader
            users get either way. */}
        {hasMore && (
          <div className="visitor-list__more" ref={sentinelRef}>
            {isLoadingMore ? (
              <p className="visitor-list__more-status" role="status">
                {t("react_loading")}
              </p>
            ) : (
              <button
                type="button"
                className="visitor-btn visitor-btn--secondary"
                onClick={onLoadMore}
              >
                {t("react_visitors_load_more")}
              </button>
            )}
          </div>
        )}

        {/* Only once everything is in. A count that appears mid-scroll reads as "this is all of
            them" while more are still coming. */}
        {!hasMore && !isLoading && visits.length > 0 && (
          <p className="visitor-list__count">
            {pluralText(t, "react_visitors_showing_all", total)}
          </p>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="visitor-bulk-bar">
          <span className="visitor-bulk-bar__count">
            {t("react_visitors_selected_count").replace(
              "%s",
              String(selectedIds.length)
            )}
          </span>
          <span className="visitor-bulk-bar__actions">
            <button
              type="button"
              className="visitor-bulk-bar__btn visitor-bulk-bar__btn--danger"
              onClick={() => runBulk(onBulkCancel)}
            >
              {t("react_visitors_cancel_all")}
            </button>
            <button
              type="button"
              className="visitor-bulk-bar__btn visitor-bulk-bar__btn--primary"
              disabled={!selectionIsEditable}
              onClick={() => runBulk(onBulkEdit)}
            >
              {t("react_visitors_edit_details")}
            </button>
            <button
              type="button"
              className="visitor-bulk-bar__btn"
              onClick={clearSelection}
            >
              {t("react_visitors_clear_selection")}
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

export default VisitorList;
